/**
 * Where everything in the air is going (docs/spec/02 §4, §5, §6): each enemy off its slot is flown
 * ahead by the engine's own rules (along its path, home to its slot, away off the bottom, or held
 * over a beam) and its position kept for every tick of the horizon. `tests/unit/botForecast.test.ts`
 * holds this to the engine: if `src/sim/enemy.ts` changes how enemies fly, that test says so.
 */

import * as math from '../sim/math';
import * as paths from '../sim/path';
import type { PathPoint } from '../sim/path';
import * as sim from '../sim';
import type { Enemy, GameState } from '../sim';
import type { Forecast } from './forecast';
import * as horizon from './horizon';

// Local copies (see horizon.ts).
const { sin, cos } = math;
const { pathPoint } = paths;
const { BEAM, ENEMY_HIT, ENTRY, FIELD } = sim;
const { HORIZON, MAX_ENEMIES, OFF, STRIDE } = horizon;

const MAX_TRACKS = 64;

const PATH = 0;
const HOME = 1;
const SEATED = 2;
const LEAVE = 3;
const HOVER = 4;
/** Gone, or about to do something only the game's own dice can tell. */
const LOST = 5;

const HOME_COS = cos(ENTRY.homeTurn);
const HOME_SIN = sin(ENTRY.homeTurn);
/** The engine's `leave` eases downward by this much a tick. */
const LEAVE_TURN = 0.03;
const LEAVE_COS = cos(LEAVE_TURN);
const LEAVE_SIN = sin(LEAVE_TURN);
/** A whole beam, from the Monarch stopping to its moving on. */
const BEAM_TICKS = BEAM.extend + BEAM.hold + BEAM.retract;

const P: PathPoint = { x: 0, y: 0, h: 0 };

/** Hive moves until a hovering Monarch gives up its beam and flies on. */
function hoverLeft(s: GameState, e: Enemy): number {
  const beam = s.beam;
  if (!beam || beam.boss !== e.id) return BEAM_TICKS + 1;
  if (beam.phase === 'extend') return BEAM_TICKS - beam.t;
  if (beam.phase === 'hold') return BEAM.hold + BEAM.retract - beam.t;
  return BEAM.retract - beam.t;
}

export class Tracks {
  count = 0;
  /** `x[track * STRIDE + k]`: where it is after `k` ticks. `y` is `OFF` while it cannot be touched. */
  readonly x = new Float32Array(MAX_TRACKS * STRIDE);
  readonly y = new Float32Array(MAX_TRACKS * STRIDE);
  /** Per track: the enemy's index in `state.enemies`, and half its hit box. */
  readonly who = new Int16Array(MAX_TRACKS);
  readonly half = new Uint8Array(MAX_TRACKS);
  /** Per track: the tick it takes its seat in the formation; past the horizon if it does not. */
  readonly seated = new Uint8Array(MAX_TRACKS);

  /** The enemy being flown ahead: how it is flying, where it is, its heading as a unit vector. */
  private mode = LOST;
  private px = 0;
  private py = 0;
  private hx = 0;
  private hy = 0;
  private dist = 0;
  /** Hive moves left over its beam; whether it comes back in at the top after leaving. */
  private hover = 0;
  private wraps = false;

  build(s: GameState, f: Forecast): void {
    this.count = 0;
    const n = Math.min(s.enemies.length, MAX_ENEMIES);
    for (let i = 0; i < n && this.count < MAX_TRACKS; i++) {
      const e = s.enemies[i];
      if (!e || e.gone || e.state === 'formed' || e.state === 'morphing') continue;
      this.fly(s, f, e, i);
    }
  }

  private fly(s: GameState, f: Forecast, e: Enemy, index: number): void {
    const track = this.count++;
    const base = track * STRIDE;
    this.who[track] = index;
    this.half[track] = e.kind === 'monarch' ? ENEMY_HIT.monarch : ENEMY_HIT.small;
    this.seated[track] = HORIZON + 1;
    this.mode = e.state === 'homing' ? HOME : e.state === 'hover' ? HOVER : PATH;
    if (e.state === 'leaving' || (this.mode === PATH && !e.path)) this.mode = LEAVE;
    this.wraps = e.after === 'wrap' && e.slotted;
    this.hover = this.mode === HOVER ? hoverLeft(s, e) : 0;
    this.px = e.x;
    this.py = e.y;
    this.dist = e.dist;
    this.hx = cos(e.heading);
    this.hy = sin(e.heading);
    this.x[base] = e.x;
    // A trio's followers wait off stage at the start of their path.
    this.y[base] = e.dist < 0 ? OFF : e.y;
    for (let k = 1; k <= HORIZON; k++) {
      if (f.adv[k] !== f.adv[k - 1]) {
        if (this.mode === HOVER && --this.hover < 0) {
          // The beam is in: it carries on down and comes back from the top.
          this.mode = LEAVE;
          this.hx = 0;
          this.hy = 1;
          this.wraps = e.slotted;
        }
        if (this.mode === PATH) this.along(e);
        else if (this.mode === HOME) this.home(f, e, k, track);
        else if (this.mode === LEAVE) this.leave(f, e, k);
        else if (this.mode === SEATED) {
          this.px = f.slotX(k, e.col);
          this.py = f.slotY(k, e.row);
        }
      }
      this.x[base + k] = this.px;
      this.y[base + k] = this.mode === LOST || this.dist < 0 ? OFF : this.py;
    }
  }

  /** The engine's `fly` and `place`: along the path, shifted as an escort or a second file is. */
  private along(e: Enemy): void {
    const path = e.path;
    if (!path) return;
    this.dist += e.speed;
    pathPoint(path, this.dist, P);
    this.px = P.x + e.ox;
    this.py = P.y + e.oy;
    if (e.side !== 0) {
      this.px -= sin(P.h) * e.side;
      this.py += cos(P.h) * e.side;
    }
    if (this.dist < path.length) return;
    this.hx = cos(P.h);
    this.hy = sin(P.h);
    if (e.after === 'home') this.mode = e.slotted ? HOME : LEAVE;
    else if (e.after === 'beam') {
      this.mode = HOVER;
      this.hover = BEAM_TICKS + 1;
    } else this.mode = e.after === 'attack' ? LOST : LEAVE;
  }

  /** The engine's `home`: steer to the slot, turning only so fast, and snap in. */
  private home(f: Forecast, e: Enemy, k: number, track: number): void {
    const tx = f.slotX(k, e.col);
    const ty = f.slotY(k, e.row);
    const dx = tx - this.px;
    const dy = ty - this.py;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= e.speed + 0.5) {
      this.px = tx;
      this.py = ty;
      this.mode = SEATED;
      this.seated[track] = k;
      return;
    }
    const ux = dx / d;
    const uy = dy / d;
    if (d < ENTRY.homeSnap || this.hx * ux + this.hy * uy >= HOME_COS) {
      this.hx = ux;
      this.hy = uy;
    } else this.turn(this.hx * uy - this.hy * ux >= 0 ? HOME_SIN : -HOME_SIN, HOME_COS);
    this.px += this.hx * e.speed;
    this.py += this.hy * e.speed;
  }

  /** The engine's `leave`: on, easing downward, until off the screen; then back in at the top, or gone. */
  private leave(f: Forecast, e: Enemy, k: number): void {
    if (this.hy >= LEAVE_COS) {
      this.hx = 0;
      this.hy = 1;
    } else this.turn(this.hx >= 0 ? LEAVE_SIN : -LEAVE_SIN, LEAVE_COS);
    this.px += this.hx * e.speed;
    this.py += this.hy * e.speed;
    if (this.py <= FIELD.h + 12 && this.py >= -40 && this.px >= -24 && this.px <= FIELD.w + 24) return;
    if (!this.wraps) {
      this.mode = LOST;
      return;
    }
    this.px = f.slotX(k, e.col);
    this.py = -12;
    this.hx = 0;
    this.hy = 1;
    this.mode = HOME;
  }

  /** Turn the heading by the angle whose sine and cosine these are (positive is clockwise on screen). */
  private turn(sine: number, cosine: number): void {
    const nx = this.hx * cosine - this.hy * sine;
    this.hy = this.hx * sine + this.hy * cosine;
    this.hx = nx;
  }
}
